import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper,
    Tabs,
    Tab,
    Chip,
} from '@mui/material';
import { AuthContext } from "../context/AuthContext";
import { ErrorContext } from "../context/ErrorContext";
import { createAxiosInstance } from "../api/axios";
import { useNavigate } from 'react-router-dom';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GradeScoreDisplay from './GradeScoreDisplay';


const Dashboard = () => {
    const { user, loading } = useContext(AuthContext);
    const { setError } = useContext(ErrorContext);
    const navigate = useNavigate();
    
    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);
    
    // Application states
    const [applications, setApplications] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);

    const authAxios = useMemo(() => {
        return createAxiosInstance(null);
    }, []);

    // Fetch user applications
    const fetchApplications = useCallback(async () => {
        if (!user) return;
        try {
            setApplicationsLoading(true);
            const response = await authAxios.get('/application/', { params: { limit: 100 } });
            setApplications(response.data.results);
        } catch (error) {
            console.error('Failed to fetch applications:', error);
            setError("Failed to fetch applications");
        } finally {
            setApplicationsLoading(false);
        }
    }, [user, authAxios, setError]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    useEffect(() => {
        if (user && !loading) {
            fetchApplications();
        }
    }, [user, loading, fetchApplications]);

    if (loading) {
        return (
            <Box sx={{
                minHeight: "100vh",
                backgroundColor: "white",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
            }}>
                <CircularProgress size={60} sx={{ color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Loading your dashboard
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "white",
                py: 4,
                px: 3,
            }}
        >
            {/* Welcome Message */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{
                        color: 'black',
                        mb: 1
                    }}
                >
                    Welcome, {user?.first_name} {user?.last_name}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.secondary'
                    }}
                >
                    Your Talent Match Dashboard
                </Typography>
            </Box>

            {/* Main Content with Tabs */}
            <Paper sx={{ 
                background: 'white',
                borderRadius: 0,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
            }}>
                {/* Tabs Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={selectedTab} 
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                py: 2,
                            }
                        }}
                    >
                        <Tab 
                            icon={<AssignmentTurnedInIcon />} 
                            iconPosition="start" 
                            label="Applications" 
                        />
                        <Tab 
                            icon={<AssessmentIcon />} 
                            iconPosition="start" 
                            label="Assessments" 
                        />
                        <Tab 
                            icon={<BookmarkIcon />} 
                            iconPosition="start" 
                            label="Saved" 
                        />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ p: 3 }}>
                    {/* Applications Tab */}
                    {selectedTab === 0 && (
                        <Box>
                            {/* <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight={600} color="primary.main" gutterBottom>
                                    My Applications
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Track your job applications and their status
                                </Typography>
                            </Box> */}
                            
                            {applicationsLoading ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <CircularProgress size={40} sx={{ color: 'primary.main', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        Loading applications...
                                    </Typography>
                                </Box>
                            ) : applications.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <AssignmentTurnedInIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" fontStyle="italic" gutterBottom>
                                        No applications found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Start applying to jobs to see them here
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate('/')}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 4,
                                            py: 1.5,
                                        }}
                                    >
                                        Browse Jobs
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    {/* Table Header */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        borderBottom: '2px solid #e5e7eb',
                                        py: 1.5,
                                        px: 2,
                                        bgcolor: '#f9fafb',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        color: '#374151',
                                        minWidth: '700px',
                                    }}>
                                        <Box sx={{ flex: 2, minWidth: '200px' }}>Job Title</Box>
                                        <Box sx={{ flex: 1.5, minWidth: '150px' }}>Location</Box>
                                        <Box sx={{ flex: 1, minWidth: '100px' }}>Status</Box>
                                        <Box sx={{ flex: 1.5, minWidth: '150px', textAlign: 'center' }}>Scores</Box>
                                    </Box>
                                    
                                    {/* Table Rows */}
                                    {applications.map((application, idx) => {
                                        const getStatusColor = (status) => {
                                            switch (status?.toLowerCase()) {
                                                case 'accepted': return 'success';
                                                case 'rejected': return 'error';
                                                case 'interview': return 'primary';
                                                default: return 'default';
                                            }
                                        };
                                        
                                        return (
                                            <Box 
                                                key={idx}
                                                onClick={() => window.open(`/my-application/${application.uuid}`, '_blank')}
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2,
                                                    px: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    minWidth: '700px',
                                                    '&:hover': {
                                                        bgcolor: '#f3f4f6',
                                                    },
                                                    '&:last-child': {
                                                        borderBottom: 'none',
                                                    }
                                                }}
                                            >
                                                {/* Job Title */}
                                                <Box sx={{ flex: 2, minWidth: '200px', pr: 2 }}>
                                                    <Typography 
                                                        fontWeight={600} 
                                                        color="text.primary"
                                                        sx={{ 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.9rem',
                                                        }}
                                                    >
                                                        {application.job_title}
                                                    </Typography>
                                                    {/* <Typography variant="caption" color="text.secondary">
                                                        {application.organisation_slug || 'Priyo'}
                                                    </Typography> */}
                                                </Box>
                                                
                                                {/* Location */}
                                                <Box sx={{ flex: 1.5, minWidth: '150px', pr: 2 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        color="text.secondary"
                                                        sx={{ 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {application.location || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                
                                                {/* Status */}
                                                <Box sx={{ flex: 1, minWidth: '100px', pr: 2 }}>
                                                    {application.status && (
                                                        <Chip
                                                            label={application.status.charAt(0).toUpperCase() + application.status.slice(1).toLowerCase()}
                                                            color={getStatusColor(application.status)}
                                                            size="small"
                                                            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                                                        />
                                                    )}
                                                </Box>
                                                
                                                {/* Scores */}
                                                <Box sx={{ flex: 1.5, minWidth: '150px', display: 'flex', justifyContent: 'center' }}>
                                                    {(application.profile_grade || application.profile_score) ? (
                                                        <GradeScoreDisplay 
                                                            grade={application.application_grade} 
                                                            score={application.application_score}
                                                            size="small"
                                                            showLabels={false}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            —
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Assessments Tab */}
                    {selectedTab === 1 && (
                        <Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight={600} color="primary.main" gutterBottom>
                                    My Assessments
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    View and track your assessment results
                                </Typography>
                            </Box>
                            
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <AssessmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" fontStyle="italic">
                                    Coming Soon
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Assessment tracking will be available here
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Saved Tab */}
                    {selectedTab === 2 && (
                        <Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight={600} color="primary.main" gutterBottom>
                                    Saved Jobs
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    View jobs you've saved for later
                                </Typography>
                            </Box>
                            
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <BookmarkIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" fontStyle="italic">
                                    Coming Soon
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Saved jobs will be available here
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default Dashboard;
